import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  writeBatch,
  updateDoc,
  deleteField,
  increment,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { logError } from "../services/errorService";

// URL de las funciones de Firebase
const FIREBASE_FUNCTIONS_URL = "https://us-central1-gana-con-bendicion-en-mano.cloudfunctions.net";

// Función para llamar a las funciones de Firebase
const callFirebaseFunction = async (functionName, data) => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Origin: window.location.origin,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || "Error desconocido";
      } catch {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error al llamar función ${functionName}:`, error);
    throw error;
  }
};

const purchasesCollection = collection(db, "purchases");

/**
 * Valida los datos de una compra
 * @param {Object} data - Datos de la compra
 * @throws {Error} Si los datos no son válidos
 */
const validatePurchaseData = (data) => {
  const { cedula, nombre, correo, telefono, referencia, totalAmount, quantity } = data;
  if (!cedula || !nombre || !correo || !telefono || !referencia) {
    throw new Error("Todos los campos de usuario son obligatorios.");
  }
  if (quantity <= 0) {
    throw new Error("La cantidad de tickets debe ser mayor a 0.");
  }
  if (totalAmount <= 0) {
    throw new Error("El monto total debe ser mayor a 0.");
  }
  if (!referencia) {
    throw new Error("La referencia de pago es obligatoria.");
  }
  // Validar formato de correo
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(correo)) {
    throw new Error("El formato del correo electrónico no es válido.");
  }
  // Validar formato de teléfono
  const phoneRegex = /^\+?[\d\s-]{10,}$/;
  if (!phoneRegex.test(telefono)) {
    throw new Error("El formato del teléfono no es válido.");
  }
};

/**
 * Obtiene todas las compras
 * @returns {Promise<Array>} Lista de compras
 */
export async function fetchPurchases() {
  try {
    const snapshot = await getDocs(purchasesCollection);
    return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    await logError(error, {
      action: 'fetch_purchases',
      stage: 'fetching_all'
    });
    throw new Error('Error al obtener las compras. Por favor, intente nuevamente.');
  }
}

/**
 * Obtiene las compras de una rifa específica
 * @param {string} rifaId - ID de la rifa
 * @returns {Promise<Array>} Lista de compras de la rifa
 */
export async function fetchPurchasesByRifa(rifaId) {
  try {
    const q = query(purchasesCollection, where('rifaId', '==', rifaId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    await logError(error, {
      action: 'fetch_purchases',
      stage: 'fetching_by_rifa',
      rifaId
    });
    throw new Error('Error al obtener las compras de la rifa. Por favor, intente nuevamente.');
  }
}

/**
 * Valida una compra y actualiza el estado de los tickets
 * @param {string} purchaseId - ID de la compra
 * @returns {Promise<void>}
 */
export async function validatePurchase(purchaseId) {
  try {
    const purchaseRef = doc(db, 'purchases', purchaseId);
    const snap = await getDoc(purchaseRef);
    
    if (!snap.exists()) {
      throw new Error('La compra no existe.');
    }
    
    const purchase = snap.data();
    const batch = writeBatch(db);
    
    purchase.tickets.forEach(t => {
      const ticketRef = doc(db, 'rifas', purchase.rifaId, 'tickets', t.ticketId);
      batch.update(ticketRef, {
        status: 'validado',
        purchaseId,
        cedula: purchase.cedula,
        nombre: purchase.nombre,
        telefono: purchase.telefono,
        email: purchase.correo,
        referencia: purchase.referencia,
        fechaPago: purchase.paymentDate || serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    
    batch.update(purchaseRef, { 
      status: 'pagado', 
      updatedAt: serverTimestamp() 
    });
    
    await batch.commit();
    
    await callFirebaseFunction('sendPurchaseValidatedEmail', {
      correo: purchase.correo,
      nombre: purchase.nombre,
      purchaseId: purchaseRef.id,
      tickets: purchase.tickets,
    });
  } catch (error) {
    await logError(error, {
      action: 'validate_purchase',
      stage: 'validation',
      purchaseId
    });
    throw new Error('Error al validar la compra. Por favor, intente nuevamente.');
  }
}

/**
 * Rechaza un pago y actualiza el estado de los tickets
 * @param {string} purchaseId - ID de la compra
 * @returns {Promise<void>}
 */
export async function rejectPayment(purchaseId) {
  // console.log('rejectPayment: comenzando rechazo de pago', purchaseId);
  try {
    const purchaseRef = doc(db, 'purchases', purchaseId);
    const snap = await getDoc(purchaseRef);
    
    if (!snap.exists()) {
      throw new Error('La compra no existe.');
    }
    
    const purchase = snap.data();
    const batch = writeBatch(db);
    
    purchase.tickets.forEach(t => {
      const ticketRef = doc(db, 'rifas', purchase.rifaId, 'tickets', t.ticketId);
      batch.update(ticketRef, { 
        status: 'rechazado', 
        updatedAt: serverTimestamp() 
      });
    });
    
    batch.update(purchaseRef, { 
      status: 'rechazado', 
      updatedAt: serverTimestamp() 
    });
    
    await batch.commit();
    // console.log('rejectPayment: pago rechazado correctamente', purchaseId);
    
    await callFirebaseFunction('sendPurchaseRejectionEmail', {
      correo: purchase.correo,
      nombre: purchase.nombre,
      purchaseId: purchaseRef.id,
      reason: 'Pago rechazado',
    });
  } catch (error) {
    await logError(error, {
      action: 'reject_payment',
      stage: 'rejection',
      purchaseId
    });
    throw new Error('Error al rechazar el pago. Por favor, intente nuevamente.');
  }
}

/**
 * Cancela una compra y libera los tickets
 * @param {string} purchaseId - ID de la compra
 * @returns {Promise<void>}
 */
export async function cancelPurchase(purchaseId) {
  try {
    const purchaseRef = doc(db, 'purchases', purchaseId);
    const snap = await getDoc(purchaseRef);
    
    if (!snap.exists()) {
      throw new Error('La compra no existe.');
    }
    
    const purchase = snap.data();
    const batch = writeBatch(db);
    
    purchase.tickets.forEach(t => {
      const ticketRef = doc(db, 'rifas', purchase.rifaId, 'tickets', t.ticketId);
      batch.update(ticketRef, {
        status: 'disponible',
        purchaseId: deleteField(),
        cedula: deleteField(),
        nombre: deleteField(),
        telefono: deleteField(),
        email: deleteField(),
        referencia: deleteField(),
        comprobanteUrl: deleteField(),
        fechaPago: deleteField(),
        updatedAt: serverTimestamp(),
      });
    });
    
    batch.update(doc(db, 'rifas', purchase.rifaId), { 
      ticketsCount: increment(purchase.tickets.length) 
    });
    
    batch.update(purchaseRef, { 
      status: 'cancelado', 
      updatedAt: serverTimestamp() 
    });
    
    await batch.commit();
  } catch (error) {
    await logError(error, {
      action: 'cancel_purchase',
      stage: 'cancellation',
      purchaseId
    });
    throw new Error('Error al cancelar la compra. Por favor, intente nuevamente.');
  }
}

/**
 * Realiza una compra manual
 * @param {string} rifaId - ID de la rifa
 * @param {Object} userData - Datos del usuario
 * @param {number} quantity - Cantidad de tickets
 * @returns {Promise<string>} ID de la compra
 */
export async function manualPurchase(rifaId, userData, quantity) {
  try {
    const { cedula, nombre, correo, telefono, referencia, totalAmount, receiptUrl } = userData;
    
    //console.log("Datos recibidos:", {
    //   rifaId,
    //   userData,
    //   quantity,
    // });
    
    // Validar que totalAmount sea un número válido
    if (!totalAmount || isNaN(Number(totalAmount))) {
      throw new Error("El monto total es inválido");
    }

    validatePurchaseData({ ...userData, quantity, totalAmount: Number(totalAmount) });
    
    // Obtener el total de tickets disponibles
    const ticketsQuery = query(
      collection(db, `rifas/${rifaId}/tickets`),
      where("status", "==", "disponible")
    );
    
    const ticketsSnapshot = await getDocs(ticketsQuery);
    if (ticketsSnapshot.size < quantity) {
      throw new Error("No hay suficientes tickets disponibles.");
    }

    // Seleccionar tickets aleatorios
    const reservedTickets = [];
    const availableTickets = ticketsSnapshot.docs;
    const totalTickets = availableTickets.length;
    
    // Usar un Set para asegurar índices únicos
    const selectedIndices = new Set();
    
    while (reservedTickets.length < quantity) {
      const randomIndex = Math.floor(Math.random() * totalTickets);
      
      // Si el índice ya fue seleccionado, continuar con el siguiente
      if (selectedIndices.has(randomIndex)) continue;
      
      selectedIndices.add(randomIndex);
      const ticket = availableTickets[randomIndex];
      const ticketData = ticket.data();
      
      // Verificar que el ticket tenga todos los campos necesarios
      if (!ticketData.numeroTicket) {
        console.warn(`Ticket ${ticket.id} no tiene número de ticket, saltando...`);
        continue;
      }
      
      reservedTickets.push({
        ticketId: ticket.id,
        numeroTicket: ticketData.numeroTicket,
      });
    }
    
    // Verificar que tenemos la cantidad correcta de tickets
    if (reservedTickets.length !== quantity) {
      throw new Error("No se pudieron reservar todos los tickets solicitados.");
    }
    
    reservedTickets.sort(() => Math.random() - 0.5);
    
    //console.log("Tickets reservados:", reservedTickets);
    
    const batch = writeBatch(db);
    const purchaseRef = doc(collection(db, "purchases"));
    
    // Asegurarnos de que todos los campos requeridos tengan valores válidos
    const purchaseData = {
      rifaId,
      cedula: cedula || "",
      nombre: nombre || "",
      correo: correo || "",
      telefono: telefono || "",
      referencia: referencia || "",
      tickets: reservedTickets,
      totalAmount: Number(totalAmount),
      status: "pendiente",
      paymentDate: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      receiptUrl: receiptUrl || "",
      comprobanteUrl: receiptUrl || "",
      email: correo || "", // Campo requerido por las reglas de Firestore
      fechaPago: serverTimestamp(), // Campo requerido por las reglas de Firestore
      fecha: serverTimestamp(), // Campo requerido por las reglas de Firestore
    };
    
    //console.log("Datos de la compra a guardar:", {
    //   ...purchaseData,
    //   paymentDate: "serverTimestamp()",
    //   createdAt: "serverTimestamp()",
    //   updatedAt: "serverTimestamp()",
    //   fechaPago: "serverTimestamp()",
    //   fecha: "serverTimestamp()",
    // });
    
    batch.set(purchaseRef, purchaseData);
    
    reservedTickets.forEach((ticket) => {
      const ticketRef = doc(db, `rifas/${rifaId}/tickets/${ticket.ticketId}`);
      const ticketData = {
        status: "pendiente",
        purchaseId: purchaseRef.id,
        cedula: cedula || "",
        nombre: nombre || "",
        correo: correo || "",
        telefono: telefono || "",
        referencia: referencia || "",
        paymentDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      //console.log(`Actualizando ticket ${ticket.ticketId}:`, {
      //   ...ticketData,
      //   paymentDate: "serverTimestamp()",
      //   updatedAt: "serverTimestamp()",
      // });
      
      batch.update(ticketRef, ticketData);
    });
    
    batch.update(doc(db, "rifas", rifaId), {
      ticketsCount: increment(-quantity),
    });
    
    await batch.commit();
    //console.log("Compra guardada exitosamente con ID:", purchaseRef.id);
    
    // Intentar enviar correos de forma asíncrona y no bloquear el proceso
    try {
      await Promise.all([
        callFirebaseFunction('sendPurchaseConfirmationHttp', {
          correo: correo,
          nombre: nombre,
          purchaseId: purchaseRef.id,
          tickets: reservedTickets,
          totalAmount: Number(totalAmount),
          referencia: referencia,
        }),
        callFirebaseFunction('notifyAdminNewPurchaseHttp', {
          id: purchaseRef.id,
          rifaId: rifaId,
          cedula: cedula,
          nombre: nombre,
          correo: correo,
          telefono: telefono,
          referencia: referencia,
          montoTotal: Number(totalAmount),
          tickets: reservedTickets,
          comprobanteUrl: receiptUrl || "",
        }),
      ]);
      //console.log("Correos enviados exitosamente");
    } catch (emailError) {
      console.error("Error al enviar correos:", emailError);
      // Registrar el error en la base de datos
      await logError(
        emailError,
        {
          action: "send_emails",
          stage: "after_purchase",
          purchaseId: purchaseRef.id,
        },
        {
          cedula,
          nombre,
          correo,
          telefono,
        }
      );
    }
    
    return purchaseRef.id;
  } catch (error) {
    console.error("Error en manualPurchase:", error);
    await logError(
      error,
      {
        action: "manual_purchase",
        stage: "purchase",
        rifaId,
        quantity,
      },
      {
        cedula: userData.cedula,
        nombre: userData.nombre,
        correo: userData.correo,
        telefono: userData.telefono,
      }
    );
    throw error;
  }
}

/**
 * Verifica si una cédula tiene pagos pendientes
 * @param {string} cedula - Cédula del comprador
 * @returns {Promise<{hasPending: boolean, pendingCount: number, message: string}>} 
 */
export const hasPendingPayments = async (cedula) => {
  try {
    const purchasesQuery = query(
      collection(db, 'purchases'),
      where('cedula', '==', cedula),
      where('status', '==', 'pendiente')
    );
    
    const snapshot = await getDocs(purchasesQuery);
    const pendingCount = snapshot.size;
    
    if (pendingCount >= 3) {
      return {
        hasPending: true,
        pendingCount,
        message: 'Has alcanzado el límite máximo de 3 pagos pendientes. Por favor, espera a que sean validados antes de realizar una nueva compra.'
      };
    }
    
    return pendingCount > 0;
  } catch (error) {
    await logError(error, {
      action: 'check_pending_payments',
      stage: 'verification',
      cedula
    });
    throw error;
  }
};

/**
 * Crea una nueva compra
 * @param {Object} purchaseData - Datos de la compra
 * @returns {Promise<Object>} Compra creada
 */
export const createPurchase = async (purchaseData) => {
  try {
    // Validar datos de la compra
    validatePurchaseData(purchaseData);

    // Verificar si hay pagos pendientes
    const hasPending = await hasPendingPayments(purchaseData.cedula);
    if (hasPending) {
      throw new Error('Tienes pagos pendientes por validar. Por favor, espera a que sean validados antes de realizar una nueva compra.');
    }

    // Verificar disponibilidad de tickets
    const availableTickets = await getAvailableTickets(purchaseData.rifaId, purchaseData.tickets.length);
    if (availableTickets.length !== purchaseData.tickets.length) {
      throw new Error('Algunos tickets seleccionados ya no están disponibles.');
    }

    // Crear la compra
    const purchaseRef = doc(collection(db, 'purchases'));
    
    // Asegurarnos de que todos los campos requeridos tengan valores válidos
    const purchase = {
      ...purchaseData,
      id: purchaseRef.id,
      status: 'pendiente',
      fecha: serverTimestamp(),
      cedula: purchaseData.cedula || "",
      nombre: purchaseData.nombre || "",
      correo: purchaseData.correo || "",
      telefono: purchaseData.telefono || "",
      referencia: purchaseData.referencia || "",
      email: purchaseData.correo || "", // Campo requerido por las reglas de Firestore
      fechaPago: serverTimestamp(), // Campo requerido por las reglas de Firestore
      comprobanteUrl: purchaseData.comprobanteUrl || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(purchaseRef, purchase);

    // Actualizar estado de los tickets
    const batch = writeBatch(db);
    purchaseData.tickets.forEach(ticket => {
      const ticketRef = doc(db, `rifas/${purchaseData.rifaId}/tickets/${ticket.id}`);
      batch.update(ticketRef, { 
        status: 'pendiente',
        purchaseId: purchaseRef.id,
        cedula: purchaseData.cedula || "",
        nombre: purchaseData.nombre || "",
        correo: purchaseData.correo || "",
        telefono: purchaseData.telefono || "",
        referencia: purchaseData.referencia || "",
        paymentDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    await batch.commit();

    return purchase;
  } catch (error) {
    await logError(error, {
      action: 'create_purchase',
      stage: 'creation',
      rifaId: purchaseData.rifaId
    }, {
      cedula: purchaseData.cedula,
      nombre: purchaseData.nombre,
      correo: purchaseData.correo,
      telefono: purchaseData.telefono
    });
    throw error;
  }
};

// Reassign tickets for a purchase: free old tickets and assign new ones
export async function reassignTickets(purchaseId, rifaId, newTicketNumbers) {
  const purchaseRef = doc(db, "purchases", purchaseId);
  const purchaseSnap = await getDoc(purchaseRef);
  if (!purchaseSnap.exists()) throw new Error("Compra no encontrada");
  const purchase = purchaseSnap.data();
  const batch = writeBatch(db);
  const raffleRef = doc(db, "rifas", rifaId);

  // Free old tickets
  purchase.tickets.forEach((t) => {
    const oldRef = doc(db, "rifas", rifaId, "tickets", t.ticketId);
    batch.update(oldRef, {
      status: "disponible",
      purchaseId: deleteField(),
      cedula: deleteField(),
      nombre: deleteField(),
      telefono: deleteField(),
      email: deleteField(),
      referencia: deleteField(),
      comprobanteUrl: deleteField(),
      fechaPago: deleteField(),
      updatedAt: Timestamp.now(),
    });
  });
  // Ajustar contador al liberar tickets antiguos
  batch.update(raffleRef, { ticketsCount: increment(purchase.tickets.length) });

  // Fetch docs for new ticket numbers
  const ticketsCol = collection(db, "rifas", rifaId, "tickets");
  const q = query(ticketsCol, where("numeroTicket", "in", newTicketNumbers));
  const snap = await getDocs(q);
  const newDocs = snap.docs;
  if (newDocs.length !== newTicketNumbers.length)
    throw new Error("Algunos tickets no están disponibles");

  // Assign new tickets with purchase status
  const newTicketsData = newDocs.map((docSnap) => ({
    ticketId: docSnap.id,
    numeroTicket: docSnap.data().numeroTicket,
  }));
  const ticketStatus = purchase.status || "pendiente";
  newDocs.forEach((docSnap) => {
    const tRef = doc(db, "rifas", rifaId, "tickets", docSnap.id);
    batch.update(tRef, {
      status: ticketStatus,
      purchaseId,
      cedula: purchase.cedula,
      nombre: purchase.nombre,
      telefono: purchase.telefono,
      email: purchase.correo,
      referencia: purchase.referencia,
      comprobanteUrl: purchase.receiptUrl || purchase.comprobanteUrl || "",
      fechaPago: purchase.paymentDate || Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  });
  // Ajustar contador al asignar nuevos tickets
  batch.update(raffleRef, { ticketsCount: increment(-newTicketsData.length) });

  // Update purchase doc with new tickets array
  batch.update(purchaseRef, { tickets: newTicketsData, updatedAt: Timestamp.now() });
  await batch.commit();
}


// Actualizar campos de una compra
export async function updatePurchase(purchaseId, data) {
  const purchaseRef = doc(db, "purchases", purchaseId);
  const updatedData = { ...data, updatedAt: Timestamp.now() };
  await updateDoc(purchaseRef, updatedData);
}

// Free tickets of a rejected purchase: mark as available and clear fields
export async function freeTickets(purchaseId) {
  const purchaseRef = doc(db, "purchases", purchaseId);
  const purchaseSnap = await getDoc(purchaseRef);
  if (!purchaseSnap.exists()) throw new Error("Compra no encontrada");
  const purchase = purchaseSnap.data();
  const { rifaId, tickets } = purchase;
  const batch = writeBatch(db);
  const raffleRef = doc(db, "rifas", rifaId);
  tickets.forEach((t) => {
    const ticketRef = doc(db, "rifas", rifaId, "tickets", t.ticketId);
    batch.update(ticketRef, {
      status: "disponible",
      purchaseId: deleteField(),
      cedula: deleteField(),
      nombre: deleteField(),
      telefono: deleteField(),
      email: deleteField(),
      referencia: deleteField(),
      comprobanteUrl: deleteField(),
      fechaPago: deleteField(),
      updatedAt: Timestamp.now(),
    });
  });
  // Incrementar contador de tickets disponibles
  batch.update(raffleRef, { ticketsCount: increment(tickets.length) });
  // Limpiar arreglo tickets en la compra para historial sin asociación
  batch.update(purchaseRef, { tickets: [], updatedAt: Timestamp.now() });
  await batch.commit();
}

/**
 * Actualiza los valores aleatorios de los tickets disponibles
 * @param {string} rifaId - ID de la rifa
 * @returns {Promise<void>}
 */
export async function updateRandomValues(rifaId) {
  try {
    const ticketsQuery = query(
      collection(db, `rifas/${rifaId}/tickets`),
      where('status', '==', 'disponible')
    );
    
    const ticketsSnapshot = await getDocs(ticketsQuery);
    const batch = writeBatch(db);
    
    ticketsSnapshot.docs.forEach(doc => {
      const ticketRef = doc.ref;
      batch.update(ticketRef, {
        randomValue: Math.random()
      });
    });
    
    await batch.commit();
  } catch (error) {
    await logError(error, {
      action: 'update_random_values',
      stage: 'update',
      rifaId
    });
    throw error;
  }
}